import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Button } from '../../ui';
import { panelVariants, scrimVariants } from '../../lib/motion';
import { sound } from '../../lib/sound';
import { useStrings } from '../../state/useLocale';
import type { BackupProps } from '../contracts';
import type { Progress } from '../../progress';

/**
 * Progress backup: the record as a code, and a code back into the record.
 *
 * This is the one screen in the product that admits the browser can lose
 * things. The vault keeps spare copies inside this browser and an account
 * keeps one on the server, but a learner who never signs in and then clears
 * this site's data has lost the lot, and the browser was right to take it
 * (lib/backup.ts says the rest). So the dialog offers the only answer that
 * does not depend on this browser surviving: a line of text the learner
 * takes out themselves.
 *
 * Two halves, in the order the learner needs them: the code, and the box to
 * paste one back into. The restore outcome is written into the panel rather
 * than thrown as a toast, because it is the answer to something the person
 * just did and may want to read twice — a code that did not match its
 * signature especially, which is the one outcome here that must not read
 * like a success.
 *
 * Nothing in the save half asks a question, so no control auto-focuses: the
 * panel takes focus itself and the learner chooses which half they came for.
 *
 * Both text boxes are `dir="ltr"` in both languages. A code is machine text —
 * one long opaque run of base64url with a dotted prefix on the front — and
 * inside a right-to-left page the bidi algorithm would reorder it around
 * every character it reads as punctuation, in the box the learner is copying
 * out of and in the box they are pasting into. See docs/i18n.md §7.
 */

/**
 * Cleared levels counted from the record, not from the level list. A code
 * can carry a level this build no longer ships, and the honest figure to
 * report back is the one the record holds.
 */
function clearedIn(p: Progress): number {
  let n = 0;
  for (const level of Object.values(p.levels)) if (level.completed) n++;
  return n;
}

/** Stars, counted the same way and for the same reason. */
function starsIn(p: Progress): number {
  let n = 0;
  for (const level of Object.values(p.levels)) n += level.stars;
  return n;
}

/** What the last Restore did, held until the next one. */
type Outcome =
  | {
      kind: 'restored';
      /** Whether the code matched its signature, not whether anyone is signed in. */
      signed: boolean;
      /** The standing before the merge; the delta is read off the fresh prop at render. */
      before: { levels: number; stars: number; xp: number };
    }
  | { kind: 'nothing' }
  | { kind: 'failed'; reason: string };

/*
 * How much came back used to be assembled here: two counted nouns joined
 * with `' and '`, a choice between `is` and `are`, and `toLocaleString('en-GB')`
 * on the XP. None of that is a sentence a second language can be given —
 * Arabic counts in four forms, joins with a particle rather than a word, and
 * would rather not have a verb in that position at all. So the facts go to
 * `s.standing.backup.restored` and each language writes its own sentence.
 */

export function Backup({ open, progress, restore, onClose }: BackupProps) {
  const s = useStrings().standing.backup;
  const [paste, setPaste] = useState('');
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [wasOpen, setWasOpen] = useState(open);

  const panelRef = useRef<HTMLDivElement>(null);
  const restoreTo = useRef<HTMLElement | null>(null);

  // The exporter is called from an effect and must not be a dependency of it:
  // a caller that rebuilds the function every render would otherwise export
  // the record again on every render. Held the way App's toast holds onDone.

  // Worked out during render rather than in an effect, as Warmup does: by the
  // time effects run the panel has painted, and a stale code or a stale
  // outcome from the last visit would have been on screen for a frame. A
  // reopened dialog is a fresh one — nothing here is a place you were standing.
  if (wasOpen !== open) {
    setWasOpen(open);
    if (open) {
      setPaste('');
      setOutcome(null);
      setBusy(false);
      restoreTo.current = document.activeElement as HTMLElement | null;
    }
  }


  useEffect(() => {
    if (!open) return;
    panelRef.current?.focus();
    return () => restoreTo.current?.focus?.();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }

      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      // The panel holds focus on open and is deliberately not in that list,
      // so without this the first Shift+Tab escapes behind the scrim and the
      // trap can never take it back. Same reasoning as Warmup and ClearOverlay.
      const active = document.activeElement as HTMLElement | null;
      if (!active || active === panel || !panel.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const handleRestore = useCallback(async () => {
    sound.click();
    if (busy || !paste.trim()) return;
    setBusy(true);
    // Read before the merge, from the prop this render was given. The after
    // figures are read off the next render's prop, which is the only place
    // they are certainly settled.
    const before = { levels: clearedIn(progress), stars: starsIn(progress), xp: progress.xp };
    const result = await restore(paste);
    setBusy(false);
    if (!result.ok) {
      setOutcome({ kind: 'failed', reason: result.reason });
      return;
    }
    if (!result.changed) {
      setOutcome({ kind: 'nothing' });
      return;
    }
    // Emptied on a merge that landed, so the button that is now under the
    // learner's cursor cannot be pressed again for a confusing second answer.
    setPaste('');
    setOutcome({ kind: 'restored', signed: result.signed, before });
  }, [busy, paste, progress, restore]);

  let outcomeText = '';
  let outcomeTone = '';
  if (outcome?.kind === 'restored') {
    outcomeText = s.restored({
      signed: outcome.signed,
      levels: clearedIn(progress) - outcome.before.levels,
      stars: starsIn(progress) - outcome.before.stars,
      xp: progress.xp - outcome.before.xp,
    });
    outcomeTone = outcome.signed ? ' is-good' : ' is-warn';
  } else if (outcome?.kind === 'nothing') {
    outcomeText = s.nothingNew;
  } else if (outcome?.kind === 'failed') {
    // Printed as it is. `importCode` chose a whole sentence from the
    // catalogue rather than handing back a fragment to be completed here.
    outcomeText = outcome.reason;
    outcomeTone = ' is-bad';
  }


  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="backup-scrim"
          variants={scrimVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <motion.div
            className="backup"
            ref={panelRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="backup-title"
            variants={panelVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <header className="backup-head">
              <span className="label">{s.label}</span>
              <h2 className="backup-title" id="backup-title">
                {s.title}
              </h2>
              <p className="backup-lede">{s.lede}</p>
            </header>

            <div className="backup-body scroll">
              <section className="backup-half">
                <label className="label" htmlFor="backup-paste">
                  {s.restoreLabel}
                </label>
                <textarea
                  id="backup-paste"
                  className="backup-field scroll"
                  value={paste}
                  rows={3}
                  dir="ltr"
                  spellCheck={false}
                  autoCapitalize="off"
                  autoCorrect="off"
                  placeholder={s.pastePlaceholder}
                  onChange={(e) => setPaste(e.target.value)}
                />
                <div className="backup-acts">
                  <Button variant="solid" onClick={() => void handleRestore()} disabled={busy || paste.trim() === ''}>
                    {busy ? s.restoring : s.restore}
                  </Button>
                  <span className="note">{s.onlyAdds}</span>
                </div>
                <p className={`backup-out${outcomeTone}`} role="status" aria-live="polite">
                  {outcomeText}
                </p>
              </section>
            </div>

            <footer className="backup-foot">
              <span className="note">{s.notAPassword}</span>
              <Button variant="quiet" onClick={onClose} title={s.closeWhy}>
                {s.close}
              </Button>
            </footer>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default Backup;
